import React, { useEffect, useState, useCallback } from "react";
import { Loader2, CheckCircle2, Circle, Send, Clock, XCircle, ShieldCheck, ChevronRight, Star } from "lucide-react";
import empApi from "../../lib/empApi";

const STATUS = {
  draft: { label: "Draft", cls: "bg-slate-600/30 text-slate-300" },
  pending: { label: "Menunggu Persetujuan", cls: "bg-amber-500/15 text-amber-300" },
  approved: { label: "Disetujui", cls: "bg-emerald-500/15 text-emerald-300" },
  rejected: { label: "Ditolak", cls: "bg-red-500/15 text-red-300" },
};

export default function EmpChecklist() {
  const [cl, setCl] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(null);
  const [noteOpen, setNoteOpen] = useState(null);

  const load = useCallback(async () => {
    const [c, h] = await Promise.all([
      empApi.get("/employee/checklist"),
      empApi.get("/employee/checklist/history"),
    ]);
    setCl(c.data); setHistory(h.data.history);
  }, []);
  useEffect(() => { load(); }, [load]);

  const editable = cl && (cl.status === "draft" || cl.status === "rejected");

  const toggle = async (task) => {
    if (!editable) return;
    setBusy(task.key);
    try { const { data } = await empApi.post("/employee/checklist/toggle", { key: task.key, completed: !task.completed, note: task.note }); setCl(data); }
    finally { setBusy(null); }
  };

  const saveNote = async (task, note) => {
    setBusy(task.key);
    try { const { data } = await empApi.post("/employee/checklist/toggle", { key: task.key, completed: task.completed, note }); setCl(data); setNoteOpen(null); }
    finally { setBusy(null); }
  };

  const submit = async () => {
    setBusy("submit");
    try { const { data } = await empApi.post("/employee/checklist/submit", {}); setCl(data); load(); }
    finally { setBusy(null); }
  };

  if (!cl) return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-amber-400" /></div>;
  const st = STATUS[cl.status] || STATUS.draft;

  return (
    <div className="space-y-4" data-testid="emp-checklist-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><ShieldCheck size={20} className="text-amber-400" /><h2 className="font-heading text-xl font-bold text-white">Tugas Hari Ini</h2></div>
        <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${st.cls}`} data-testid="emp-checklist-status">{st.label}</span>
      </div>

      {/* Progress */}
      <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-amber-900/30 to-slate-800/40 p-4" data-testid="emp-checklist-progress">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-xs text-slate-400">Kepatuhan (tugas wajib)</p>
            <p className="font-heading text-3xl font-bold text-white">{cl.compliance}%</p>
          </div>
          <p className="text-sm text-slate-300">Selesai {cl.mandatory_done}/{cl.mandatory_total}</p>
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-700">
          <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all" style={{ width: `${cl.compliance}%` }} />
        </div>
      </div>

      {cl.status === "rejected" && cl.reject_reason && (
        <div className="flex items-start gap-2 rounded-xl bg-red-500/15 px-4 py-2.5 text-sm text-red-300" data-testid="emp-checklist-rejected">
          <XCircle size={15} className="mt-0.5 shrink-0" /> Ditolak{cl.rejected_by ? ` oleh ${cl.rejected_by}` : ""}: {cl.reject_reason}. Perbaiki & kirim ulang.
        </div>
      )}

      {/* Tasks */}
      <div className="space-y-2" data-testid="emp-checklist-tasks">
        {cl.tasks.map((t) => (
          <div key={t.key} className="rounded-xl border border-slate-800 bg-slate-800/40 p-3" data-testid={`emp-task-${t.key}`}>
            <div className="flex items-start gap-3">
              <button onClick={() => toggle(t)} disabled={!editable || busy === t.key} data-testid={`emp-task-toggle-${t.key}`} className="mt-0.5 shrink-0">
                {busy === t.key ? <Loader2 size={20} className="animate-spin text-amber-400" /> : t.completed ? <CheckCircle2 size={20} className="text-emerald-400" /> : <Circle size={20} className="text-slate-500" />}
              </button>
              <div className="flex-1">
                <p className={`text-sm ${t.completed ? "text-slate-400 line-through" : "text-white"}`}>
                  {t.task} {t.mandatory ? <span className="ml-1 text-[10px] font-bold text-amber-300">WAJIB</span> : <span className="ml-1 text-[10px] text-slate-500">opsional</span>}
                </p>
                {t.note && <p className="mt-0.5 text-xs text-slate-400">Catatan: {t.note}</p>}
                {editable && (
                  <button onClick={() => setNoteOpen(noteOpen === t.key ? null : t.key)} className="mt-1 text-[11px] text-amber-400" data-testid={`emp-task-note-btn-${t.key}`}>{t.note ? "Ubah catatan" : "+ Catatan"}</button>
                )}
                {noteOpen === t.key && (
                  <div className="mt-1.5 flex gap-1.5">
                    <input defaultValue={t.note || ""} id={`note-${t.key}`} className="flex-1 rounded-lg border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-white" data-testid={`emp-task-note-input-${t.key}`} />
                    <button onClick={() => saveNote(t, document.getElementById(`note-${t.key}`).value)} className="rounded-lg bg-amber-500 px-2 py-1 text-xs font-bold text-slate-900">OK</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {editable && (
        <button onClick={submit} disabled={busy === "submit"} data-testid="emp-checklist-submit"
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 py-3.5 font-bold text-slate-900 disabled:opacity-50">
          {busy === "submit" ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} Kirim untuk Persetujuan
        </button>
      )}
      {cl.status === "pending" && <p className="flex items-center justify-center gap-1.5 rounded-xl bg-slate-800/40 py-3 text-sm text-amber-300" data-testid="emp-checklist-pending"><Clock size={15} /> Menunggu persetujuan Manajer Cabang.</p>}
      {cl.status === "approved" && <p className="flex items-center justify-center gap-1.5 rounded-xl bg-emerald-500/10 py-3 text-sm text-emerald-300"><CheckCircle2 size={15} /> Disetujui{cl.approved_by ? ` oleh ${cl.approved_by}` : ""}.</p>}

      {/* History */}
      {history.length > 0 && (
        <div className="rounded-2xl border border-slate-800 bg-slate-800/40 p-4" data-testid="emp-checklist-history">
          <p className="mb-2 text-sm font-semibold text-slate-300">Riwayat Kepatuhan</p>
          <div className="space-y-1">
            {history.slice(0, 10).map((h) => (
              <div key={h.date} className="flex items-center justify-between py-1 text-sm">
                <span className="text-slate-400">{h.date}</span>
                <span className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${(STATUS[h.status] || STATUS.draft).cls}`}>{(STATUS[h.status] || STATUS.draft).label}</span>
                  <span className="font-semibold text-white">{h.compliance}%</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
